/**
 * SIP Peer Group Schemas - Browser-Safe
 *
 * Zod input schemas for creating/updating SIP peer groups. These validate the
 * API payload shape and are safe to import in browser environments (pure zod,
 * no Node.js/database dependencies). The database table lives in
 * `sip/sip-peer-group.sql.ts`; these schemas mirror its columns.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

const groupTypeEnum = z.enum(['peer_group', 'trunk_group', 'carrier_group', 'teams_group', 'emergency_group']);
const loadBalancingEnum = z.enum(['round_robin', 'weighted', 'least_calls']);
const selectionModeEnum = z.enum(['failover', 'parallel']);
// ========================================
// CREATE INPUT
// ========================================

export const PeerGroupCreateInputSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255, 'Name must be 255 characters or less'),
  description: z.string().optional(),
  enabled: z.boolean().default(true),

  groupType: groupTypeEnum.default('peer_group'),
  loadBalancingStrategy: loadBalancingEnum.default('round_robin'),
  selectionMode: selectionModeEnum.default('failover'),

  maxConcurrentCalls: z.number().int().min(1).max(100000).optional(),
  healthCheckEnabled: z.boolean().default(true),

  // Load balancing / capacity
  minActiveMembers: z.number().int().min(0).nullable().optional(),
  maxCallsPerPeer: z.number().int().min(0).nullable().optional(),
  hotSwapMode: z.boolean().nullable().optional(),
  sessionAffinity: z.boolean().nullable().optional(),
  affinityTimeoutSeconds: z.number().int().min(0).nullable().optional(),

  // Failover
  autoFailover: z.boolean().nullable().optional(),
  failoverRetries: z.number().int().min(0).nullable().optional(),
  failoverDelayMs: z.number().int().min(0).nullable().optional(),
  backupGroupId: z.string().uuid('Backup group must be a valid UUID').nullable().optional(),

  // Media / SIP interface defaults
  mediaRealmId: z.string().uuid().nullable().optional(),
  preferredSipInterfaceId: z.string().uuid().nullable().optional(),
  tlsContextName: z.string().nullable().optional(),
  customHeaderLines: z.array(z.string()).default([]),

  // Metadata
  tags: z.array(z.string()).default([]),
  environment: z.string().nullable().optional(),
  carrierId: z.string().nullable().optional(),
  carrierContractId: z.string().nullable().optional(),
  carrierSupportContact: z.string().nullable().optional(),
  teamsTenantId: z.string().nullable().optional(),
  teamsDirectRoutingDomains: z.array(z.string()).default([]),
});

export type PeerGroupCreateInput = z.infer<typeof PeerGroupCreateInputSchema>;

// ========================================
// UPDATE INPUT
// ========================================

/** All fields optional for partial updates. */
export const PeerGroupUpdateInputSchema = PeerGroupCreateInputSchema.partial();

export type PeerGroupUpdateInput = z.infer<typeof PeerGroupUpdateInputSchema>;
